import { createRoutine } from 'redux-routines';
import { INIT, LOADING, SUCCESS, FAILURE } from '@constants/requestPhase';
import * as api from './api';

// ─── Routines ──────────────────────────────────────────────────────────────

const getVenueSectionsRoutine = createRoutine('RESERVATIONS/GET_VENUE_SECTIONS');
const createVenueSectionRoutine = createRoutine('RESERVATIONS/CREATE_VENUE_SECTION');
const updateVenueSectionRoutine = createRoutine('RESERVATIONS/UPDATE_VENUE_SECTION');
const deleteVenueSectionRoutine = createRoutine('RESERVATIONS/DELETE_VENUE_SECTION');

const getServicePeriodsRoutine = createRoutine('RESERVATIONS/GET_SERVICE_PERIODS');
const createServicePeriodRoutine = createRoutine('RESERVATIONS/CREATE_SERVICE_PERIOD');
const updateServicePeriodRoutine = createRoutine('RESERVATIONS/UPDATE_SERVICE_PERIOD');
const deleteServicePeriodRoutine = createRoutine('RESERVATIONS/DELETE_SERVICE_PERIOD');

const getGuestsRoutine = createRoutine('RESERVATIONS/GET_GUESTS');
const createGuestRoutine = createRoutine('RESERVATIONS/CREATE_GUEST');
const updateGuestRoutine = createRoutine('RESERVATIONS/UPDATE_GUEST');

const getReservationsRoutine = createRoutine('RESERVATIONS/GET_RESERVATIONS');
const createReservationRoutine = createRoutine('RESERVATIONS/CREATE_RESERVATION');
const updateReservationRoutine = createRoutine('RESERVATIONS/UPDATE_RESERVATION');
const cancelReservationRoutine = createRoutine('RESERVATIONS/CANCEL_RESERVATION');

const getAvailabilityRoutine = createRoutine('RESERVATIONS/GET_AVAILABILITY');

const getWaitlistRoutine = createRoutine('RESERVATIONS/GET_WAITLIST');
const addToWaitlistRoutine = createRoutine('RESERVATIONS/ADD_TO_WAITLIST');
const updateWaitlistRoutine = createRoutine('RESERVATIONS/UPDATE_WAITLIST');

// ─── Thunks ────────────────────────────────────────────────────────────────

const createThunk = (routine, request) => data => async dispatch => {
  try {
    dispatch(routine.request());
    const response = await request(data);
    return dispatch(routine.success(response.data));
  } catch (error) {
    return dispatch(routine.failure(error?.response?.data));
  } finally {
    dispatch(routine.fulfill());
  }
};

export const getVenueSections = createThunk(getVenueSectionsRoutine, api.default.getVenueSections);
export const createVenueSection = createThunk(createVenueSectionRoutine, api.default.createVenueSection);
export const updateVenueSection = createThunk(updateVenueSectionRoutine, api.default.updateVenueSection);
export const deleteVenueSection = createThunk(deleteVenueSectionRoutine, api.default.deleteVenueSection);

export const getServicePeriods = createThunk(getServicePeriodsRoutine, api.default.getServicePeriods);
export const createServicePeriod = createThunk(createServicePeriodRoutine, api.default.createServicePeriod);
export const updateServicePeriod = createThunk(updateServicePeriodRoutine, api.default.updateServicePeriod);
export const deleteServicePeriod = createThunk(deleteServicePeriodRoutine, api.default.deleteServicePeriod);

export const getGuests = (q) => async dispatch => {
  try {
    dispatch(getGuestsRoutine.request());
    const response = await api.default.getGuests(q);
    return dispatch(getGuestsRoutine.success(response.data));
  } catch (error) {
    return dispatch(getGuestsRoutine.failure(error?.response?.data));
  } finally {
    dispatch(getGuestsRoutine.fulfill());
  }
};
export const createGuest = createThunk(createGuestRoutine, api.default.createGuest);
export const updateGuest = createThunk(updateGuestRoutine, api.default.updateGuest);

export const getReservations = (date) => async dispatch => {
  try {
    dispatch(getReservationsRoutine.request());
    const response = await api.default.getReservations(date);
    return dispatch(getReservationsRoutine.success(response.data));
  } catch (error) {
    return dispatch(getReservationsRoutine.failure(error?.response?.data));
  } finally {
    dispatch(getReservationsRoutine.fulfill());
  }
};
export const createReservation = createThunk(createReservationRoutine, api.default.createReservation);
export const updateReservation = createThunk(updateReservationRoutine, api.default.updateReservation);
export const cancelReservation = createThunk(cancelReservationRoutine, api.default.cancelReservation);

export const getAvailability = (date, party_size) => async dispatch => {
  try {
    dispatch(getAvailabilityRoutine.request());
    const response = await api.default.getAvailability(date, party_size);
    return dispatch(getAvailabilityRoutine.success(response.data));
  } catch (error) {
    return dispatch(getAvailabilityRoutine.failure(error?.response?.data));
  } finally {
    dispatch(getAvailabilityRoutine.fulfill());
  }
};

export const getWaitlist = (date) => async dispatch => {
  try {
    dispatch(getWaitlistRoutine.request());
    const response = await api.default.getWaitlist(date);
    return dispatch(getWaitlistRoutine.success(response.data));
  } catch (error) {
    return dispatch(getWaitlistRoutine.failure(error?.response?.data));
  } finally {
    dispatch(getWaitlistRoutine.fulfill());
  }
};
export const addToWaitlist = createThunk(addToWaitlistRoutine, api.default.addToWaitlist);
export const updateWaitlist = createThunk(updateWaitlistRoutine, api.default.updateWaitlist);

// ─── Initial State ─────────────────────────────────────────────────────────

const initialState = {
  venueSections: [],
  getVenueSectionsPhase: INIT,
  servicePeriods: [],
  guests: [],
  reservations: [],
  availability: [],
  waitlist: [],
  selectedDate: new Date().toISOString().slice(0, 10),

  getServicePeriodsPhase: INIT,
  getGuestsPhase: INIT,
  getReservationsPhase: INIT,
  createReservationPhase: INIT,
  updateReservationPhase: INIT,
  getAvailabilityPhase: INIT,
  getWaitlistPhase: INIT,

  error: null,
};

// ─── Reducer ───────────────────────────────────────────────────────────────

const reservationsReducer = (state = initialState, action) => {
  switch (action.type) {
    case getVenueSectionsRoutine.REQUEST:
      return { ...state, getVenueSectionsPhase: LOADING };
    case getVenueSectionsRoutine.SUCCESS:
      return { ...state, getVenueSectionsPhase: SUCCESS, venueSections: action.payload };
    case getVenueSectionsRoutine.FAILURE:
      return { ...state, getVenueSectionsPhase: FAILURE };
    case createVenueSectionRoutine.SUCCESS:
      return { ...state, venueSections: [...state.venueSections, action.payload] };
    case updateVenueSectionRoutine.SUCCESS:
      return {
        ...state,
        venueSections: state.venueSections.map(s => (s.id === action.payload.id ? action.payload : s)),
      };
    case deleteVenueSectionRoutine.SUCCESS:
      return { ...state, venueSections: state.venueSections.filter(s => s.id !== action.payload.id) };

    case getServicePeriodsRoutine.REQUEST:
      return { ...state, getServicePeriodsPhase: LOADING };
    case getServicePeriodsRoutine.SUCCESS:
      return { ...state, getServicePeriodsPhase: SUCCESS, servicePeriods: action.payload };
    case getServicePeriodsRoutine.FAILURE:
      return { ...state, getServicePeriodsPhase: FAILURE };

    case createServicePeriodRoutine.SUCCESS:
      return { ...state, servicePeriods: [...state.servicePeriods, action.payload] };
    case updateServicePeriodRoutine.SUCCESS:
      return {
        ...state,
        servicePeriods: state.servicePeriods.map(p => (p.id === action.payload.id ? action.payload : p)),
      };
    case deleteServicePeriodRoutine.SUCCESS:
      return {
        ...state,
        servicePeriods: state.servicePeriods.filter(p => p.id !== action.payload.id),
      };

    case getGuestsRoutine.REQUEST:
      return { ...state, getGuestsPhase: LOADING };
    case getGuestsRoutine.SUCCESS:
      return { ...state, getGuestsPhase: SUCCESS, guests: action.payload };
    case getGuestsRoutine.FAILURE:
      return { ...state, getGuestsPhase: FAILURE };

    case createGuestRoutine.SUCCESS:
      return { ...state, guests: [...state.guests, action.payload] };
    case updateGuestRoutine.SUCCESS:
      return {
        ...state,
        guests: state.guests.map(g => (g.id === action.payload.id ? action.payload : g)),
      };

    case getReservationsRoutine.REQUEST:
      return { ...state, getReservationsPhase: LOADING };
    case getReservationsRoutine.SUCCESS:
      return { ...state, getReservationsPhase: SUCCESS, reservations: action.payload };
    case getReservationsRoutine.FAILURE:
      return { ...state, getReservationsPhase: FAILURE };

    case createReservationRoutine.REQUEST:
      return { ...state, createReservationPhase: LOADING };
    case createReservationRoutine.SUCCESS:
      return {
        ...state,
        createReservationPhase: SUCCESS,
        reservations: [...state.reservations, action.payload],
      };
    case createReservationRoutine.FAILURE:
      return { ...state, createReservationPhase: FAILURE, error: action.payload };
    case createReservationRoutine.FULFILL:
      return { ...state, createReservationPhase: INIT };

    case updateReservationRoutine.REQUEST:
      return { ...state, updateReservationPhase: LOADING };
    case updateReservationRoutine.SUCCESS:
      return {
        ...state,
        updateReservationPhase: SUCCESS,
        reservations: state.reservations.map(r => (r.id === action.payload.id ? action.payload : r)),
      };
    case updateReservationRoutine.FULFILL:
      return { ...state, updateReservationPhase: INIT };

    case cancelReservationRoutine.SUCCESS:
      return {
        ...state,
        reservations: state.reservations.filter(r => r.id !== action.payload?.id),
      };

    case getAvailabilityRoutine.REQUEST:
      return { ...state, getAvailabilityPhase: LOADING, availability: [] };
    case getAvailabilityRoutine.SUCCESS:
      return { ...state, getAvailabilityPhase: SUCCESS, availability: action.payload };
    case getAvailabilityRoutine.FAILURE:
      return { ...state, getAvailabilityPhase: FAILURE };

    case getWaitlistRoutine.REQUEST:
      return { ...state, getWaitlistPhase: LOADING };
    case getWaitlistRoutine.SUCCESS:
      return { ...state, getWaitlistPhase: SUCCESS, waitlist: action.payload };
    case getWaitlistRoutine.FAILURE:
      return { ...state, getWaitlistPhase: FAILURE };

    case addToWaitlistRoutine.SUCCESS:
      return { ...state, waitlist: [...state.waitlist, action.payload] };
    case updateWaitlistRoutine.SUCCESS:
      return {
        ...state,
        waitlist: state.waitlist.map(w => (w.id === action.payload.id ? action.payload : w)),
      };

    default:
      return state;
  }
};

export const reservationsStorePersistWhitelist = ['servicePeriods'];

export default reservationsReducer;
